import { Box, Typography } from "@mui/material";
import { DateCalendar, LocalizationProvider, PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { useCallback } from "react";

interface Props {
  value: string[]; // ["01", "05", "22"]
  onChange: (days: string[]) => void;
}

export const MonthlyDatePicker = ({ value, onChange }: Props) => {
  const today = DateTime.now();
  const monthStart = today.startOf("month");
  const monthEnd = today.endOf("month");

  const handleSelect = useCallback(
    (date: DateTime | null) => {
      if (!date) return;
      const dayStr = date.day.toString().padStart(2, "0");
      const exists = value.includes(dayStr);
      const next = exists ? value?.filter((d) => d !== dayStr) : [...value, dayStr].sort((a, b) => parseInt(a) - parseInt(b));
      onChange(next);
    },
    [value, onChange]
  );

  const CustomDay = (props: PickersDayProps<DateTime>) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const dayStr = day.day.toString().padStart(2, "0");
    const isSelected = value.includes(dayStr);
    const isOutsideMonth = day < monthStart || day > monthEnd || outsideCurrentMonth;

    return (
      <PickersDay
        {...other}
        day={day}
        disableMargin
        outsideCurrentMonth={isOutsideMonth}
        selected={false}
        onClick={() => handleSelect(day)}
        sx={{
          borderRadius: "10px",
          width: 40,
          height: 40,
          fontWeight: 600,
          backgroundColor: isSelected ? "#0071e3" : "var(--fx-surface-sunken, #F8FAFC)",
          color: isSelected ? "white" : "#1d1d1f",
          pointerEvents: isOutsideMonth ? "none" : "auto",
          transition: "background-color 0.15s ease, transform 0.1s ease",

          "&:hover": {
            backgroundColor: isSelected ? "#0077ed" : "var(--fx-surface-muted, #F1F5F9)",
            transform: "scale(1.06)",
          },

          "&, &:focus, &:focus-visible, &.Mui-focusVisible": {
            outline: "none",
            boxShadow: "none",
            backgroundColor: isSelected ? "#0071e3" : "var(--fx-surface-sunken, #F8FAFC)",
          },

          "&::after, &:focus::after, &:focus-visible::after, &.Mui-focusVisible::after": {
            content: '""', 
            display: "none",
            opacity: 0,
            background: "transparent",
            backgroundColor: "transparent",
            boxShadow: "none",
          },

          "&.Mui-selected.Mui-focusVisible": {
            backgroundColor: "#0071e3",
          },

          "&.MuiPickersDay-today": {
            border: "none",
          },
        }}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          mt: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: "var(--fx-surface-texttertiary, #94A3B8)",
            fontWeight: 500,
            fontSize: 14,
            mb: 1,
            alignSelf: "flex-start",
          }}
        >
          On days
        </Typography>

        <DateCalendar
          value={null}
          views={["day"]}
          slots={{ day: CustomDay }}
          disableHighlightToday
          sx={{
            "& .MuiPickersCalendarHeader-root": {
              pointerEvents: "none",
              opacity: 0.6,
            },
          }}
        />

        {value.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              mt: 1,
              color: "var(--fx-surface-texttertiary, #94A3B8)",
              fontSize: 12.5,
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            {value.length} {value.length === 1 ? "day" : "days"} selected
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  );
};
